import { useEffect, useState } from "react";
import { InformationCircleIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { Col, Form, RadioChangeEvent, Row, Radio, Tooltip } from "antd";
import { SymbolInfoWidget } from "../../widgets/SymbolInfo";
import { RadioGroup, RadioGroupPL } from "../RadioGroup";
import { StockTextInput } from "../StockTextInput";
import { quantityCap } from "../../utils/quantityCap";
import { StockProfitLossInput } from "../StockProfitLossInput";
import { FormItem } from "../FormItem";
import { useMediaQuery } from "src/components/FinaLayout/hooks/hooks";
import { StockNumberInput } from "../StockNumerInput";
import moment from "moment";

import {
  optionsTif,
  optionsOrderType,
  optionsProfitLossType,
  orderTypeLabel,
  sellBuyValidationRules,
  orderType,
  tifDefinition,
  sideOrder,
} from "../../utils/constants";
import { tickerInfo } from "../../utils/interfaces";
import { useGetTickerPrice } from "../../api/useGetTickerPrice";
import { SummaryModal } from "../../Layouts/SummaryModal";
import { InputLabelDescription } from "../InputLabelDescription";
import { useCustomInvestments } from "src/store/hooks";
import { formatMoney } from "src/utils/number";
import { Button as UIButton, DialogFooter, Select } from "src/components/ui";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii, space } from "src/theme/ui";

/** The form the dialog's footer submits. */
export const TRADE_FORM_ID = "trade-order-form";

/**
 * The price block, in the embed's NARROW layout.
 *
 * Scaling it down was the wrong lever and made it worse: the transform
 * divides the width back out, so the widget was handed twelve hundred
 * pixels of layout and chose its largest treatment — the company name at
 * 28px across the dialog — which the scale then only partly undid.
 *
 * These embeds pick their layout from the width they are GIVEN. Held to 400px
 * it renders the compact one: the ticker, the exchange and the price, at a
 * size that belongs in a dialog. The height crops what follows — a row of
 * fundamentals (earnings date, EPS, market cap, dividend yield, P/E) that is
 * no part of deciding how many shares to buy, and is all on the security
 * page this dialog was opened from.
 */
/**
 * The symbol block, WHOLE.
 *
 * It was clipped to 112px on the argument that a buy dialog does not need
 * upcoming earnings, EPS, market cap, dividend yield and a P/E to decide
 * how many shares to buy. The argument was fine and the crop was not: the
 * widget puts its identity on the first two lines and the PRICE on the
 * third, so cropping to the identity threw away the one figure the dialog
 * exists to price against and kept the logo.
 *
 * Uncropped, the block leads with the price, the change on the day and
 * whether the market is open, which is what a reader wants before choosing
 * a quantity. The fundamentals ride along at the end of that row; they cost
 * one line and they are the row the price lives on.
 */
/**
 * ...and set as a BAND, not as a card inside the body.
 *
 * The embed carries about 22px of padding of its own, so left in the body's
 * 20px gutter its price column began 22px to the right of the fields under
 * it: a dialog whose two halves did not share a left edge. Pulled out to the
 * body's own edges, the widget's padding lands its content on the fields'
 * line to within 2px, the block reads as the head of the dialog rather than
 * as a panel floating in it, and the 20px it gives back is 20px the form
 * does not have to scroll for.
 *
 * The rule underneath is the same hairline as the one under the dialog's
 * title, and it is what separates what the reader is looking AT from what
 * they are being asked to decide.
 */
const SymbolBox = styled.div`
  width: auto;
  margin: -${space.xl}px -${space.xl}px 0;
  border-bottom: 1px solid ${line.hairline};
`;

/**
 * What the order is bounded by, as this product sets a reading.
 *
 * The caption was uppercase 11px at 0.05em, which is a treatment the design
 * system uses nowhere: its figures wear a 11.5px/500 caption in mixed case,
 * on a panel a step darker than the dialog behind it. An uppercase caption
 * beside four mixed-case field labels is one more dialect in a dialog that
 * had three already.
 */
const Balance = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space.md}px;
  padding: 11px 14px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.control}px;
  background: ${colors.gray[50]};

  span {
    font-size: 11.5px;
    font-weight: 500;
    letter-spacing: 0.01em;
    color: ${colors.gray[600]};
  }

  b {
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[900]};
  }
`;

/**
 * A row in the time-in-force menu: the code, and what it does.
 *
 * The code alone is what the broker and every order list call it, so it
 * stays the row's subject; the second line is there for the reader who has
 * not met the acronym before, which on a retail dialog is most of them.
 */
const OptionRow = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.35;

  b {
    font-size: 12.5px;
    font-weight: 600;
    color: ${colors.gray[900]};
  }

  small {
    font-size: 11.5px;
    color: ${colors.gray[500]};
  }
`;

function disabledDate(current: moment.Moment) {
  return current && current < moment().startOf("day");
}

interface TradingFormProps {
  side: string;
  stockInfos: tickerInfo;
  onCancel: () => void;
}

export const TradingForm: React.FC<TradingFormProps> = ({
  side,
  stockInfos,
  onCancel,
}) => {
  const isMobile = useMediaQuery("(max-width: 785px)");
  const [form] = Form.useForm();
  const { symbol: tickerSymbol, name: tickerName } = stockInfos;
  const [order_type, setOrderType] = useState("Market");
  const [tif, setTif] = useState("DAY");
  const [amount, setAmount] = useState<number | undefined>(0);
  const [dateAndTime, setDateAndTime] = useState<moment.Moment>(null);
  const [profitLossType, setProfitLossType] = useState("");
  const [tickePrice, setTickerPrice] = useState(0);

  const [orderData, setOrderData] = useState({ formData: {}, otherData: {} });
  const [showSummarytModal, setShowSummarytModal] = useState(false);

  const { data: tickerPriceData } = useGetTickerPrice(tickerSymbol);

  const investmentOverView = useCustomInvestments();
  /**
   * What can actually be SPENT, not the whole cash balance.
   *
   * This read `totalCash`, which includes money already committed to a
   * pending investment or withdrawal — so the dialog offered $4,522 of
   * "available cash" against $32 the account could really use, and the
   * validation below let an order through on the strength of it. The
   * rejection would have come from the broker, after the confirmation.
   *
   * The same figure the dashboard states, worked out the same way: the
   * corrected field when the service sends it, and the subtraction as a
   * fallback for when it does not.
   */
  const balance =
    investmentOverView.data?.theCorrectAvailableCash ??
    (investmentOverView.data?.totalCash ?? 0) -
      (investmentOverView.data?.reservedCash ?? 0);

  useEffect(() => {
    if (tickerPriceData) {
      setAmount(tickerPriceData?.price);
      setTickerPrice(tickerPriceData?.price);
    }
  }, [tickerPriceData]);

  const handleSelectOrderType = (value: string) => {
    setOrderType(value);
    form.resetFields([
      "order_quantity",
      "order_limit_price",
      "tif",
      "order_stop_price",
      "trailingStep",
      "take_profit",
      "stop_loss",
    ]);
    setDateAndTime(null);
  };

  const handleChangeOrderType = ({ target: { value } }: RadioChangeEvent) => {
    setOrderType(value);
    form.resetFields([
      "order_quantity",
      "order_limit_price",
      "tif",
      "order_stop_price",
      "trailingStep",
      "take_profit",
      "stop_loss",
    ]);
    setDateAndTime(null);
  };

  const handleChangeSharesNumber = (value: number) => {
    setAmount(value * tickePrice);
  };

  const handleChangeStopPrice = (value: number) => {};
  const handleChangeLimitPrice = (value: number) => {};
  const handleChangeTrailingStep = (value: number) => {};

  /* antd composes a wrapped control's own onChange with the form's, so this
     runs on top of the form recording the value — and it reads the value it
     was handed rather than `tif`, which is still the PREVIOUS one at this
     point and left a date hanging on an order that no longer had a date. */
  const handleSelectTif = (value: string) => {
    setTif(value);
    if (!["GTD", "GAT"].includes(value)) setDateAndTime(null);
  };

  const onOk = (dateObj: moment.Moment) => {
    setDateAndTime(dateObj);
  };

  const handleChangeProfitLossType = ({
    target: { value },
  }: RadioChangeEvent) => {
    setProfitLossType(value);
  };

  const handleChangeTakeProfit = (value: number) => {};
  const handleChangeStopLoss = (value: number) => {};

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleShowSummarytModal = () => {
    setShowSummarytModal(!showSummarytModal);
  };
  const handleSubmit = (e: any) => {
    let values = form.getFieldsValue();
    // currency is hardcoded for demo purposes TODO: get it from selected client account
    values = {
      ...values,
      ticker_id: stockInfos.id,
      order_price: tickePrice,
      order_value: amount,
      currency: "USD",
      order_type: orderType[values.order_type],
    };
    if (dateAndTime !== null) values = { ...values, duration: dateAndTime };
    if (order_type === "TrailingStop") {
      values = { ...values, order_stop_price: amount - values.trailingStep };
    }
    delete values.trailingStep;
    setOrderData((prevState) => ({
      ...prevState,
      formData: values,
      otherData: {
        order_side: side,
        ticker_symbol: tickerSymbol,
        ticker_name: tickerName,
      },
    }));
    handleShowSummarytModal();
  };

  /**
   * The option lists, for the product's own select.
   *
   * Both of these were antd `Menu`s hung off a `Dropdown` under a default
   * antd `Button` — a control that looked like something to press rather
   * than something to choose from, at 40px on a 2px corner beside fields at
   * 38px on a 10px one, and one that a keyboard could not rove with the
   * arrow keys or type-to-match the way a select can.
   *
   * The per-row tooltips went with them. A tooltip inside an open menu is a
   * layer over a layer, dismissed by the very move that chooses a row, and
   * for time in force the thing it explained is the row's own second line:
   * GTC is Good-til-canceled. What the choice ITSELF means stays on the
   * label's help mark, where it can be read before the menu is open.
   */
  const tifOptions = optionsTif.map((item) => ({
    value: item.value,
    label: (
      <OptionRow>
        <b>{item.value}</b>
        <small>{item.meaning}</small>
      </OptionRow>
    ),
  }));

  const orderTypeOptions = optionsOrderType.map((item) => ({
    value: item.value,
    label: item.label,
  }));

  const stopPriceValidation = (rule, value) => {
    if (order_type === "StopLimit") {
      const limitValue = form.getFieldValue("order_limit_price");
      if (side === sideOrder.BUY && value < limitValue)
        return Promise.reject(
          "The limit price should be set at or below the stop price!",
        );
      if (side === sideOrder.SELL && value > limitValue)
        return Promise.reject(
          "The limit price should be set at or above the stop price!",
        );
    }
    return Promise.resolve();
  };

  const limitPriceValidation = (rule, value) => {
    if (order_type === "Limit") {
      // ensure that the limit price is not set absurdly high (for example 50% above or below last available price of the security)
      const diffrence = Math.abs(value - tickePrice);
      const percentage = (diffrence / tickePrice) * 100;
      if (percentage > 50)
        return Promise.reject(
          "Limit price can't exceed 50% range from last available price!",
        );
    }
    return Promise.resolve();
  };

  /**
   * The order has to fit inside the cash, fees included.
   *
   * The message said "You cannot exceed the available cash balance!", which
   * names neither figure — so a reader who had just been shown $32.42 at the
   * top of the dialog was told their order was too big without being told
   * what it came to, and had to work out the 1% themselves to see by how
   * much. Both numbers are right here.
   */
  const amountValidation = (rule, value) => {
    const withFees = amount + amount / 100;
    if (withFees > balance)
      return Promise.reject(
        `This order comes to $${formatMoney(amount)} and about $${formatMoney(
          amount / 100,
        )} in fees, more than the $${formatMoney(balance)} you have available.`,
      );
    return Promise.resolve();
  };

  const durationValidation = (rule, value) => {
    if (["GTD", "GAT"].includes(tif) && dateAndTime === null)
      return Promise.reject("Select the time! ");
    return Promise.resolve();
  };

  return (
    <>
      {/* One dialog, two steps. The summary used to open a second dialog
          over this one: two scrims, two headings, the form still visible
          behind and unreachable, and two Cancel buttons a few pixels apart
          doing different things. Placing a trade is one task. */}
      {showSummarytModal ? (
        <SummaryModal
          inline
          isSummaryModalVisible
          setSummaryModalVisible={handleShowSummarytModal}
          order={orderData}
          onCancel={onCancel}
        />
      ) : null}
      {showSummarytModal ? null : (
        /* Step one's actions, in the dialog's footer band. They belong to
           the step rather than to the shell, which cannot know which of the
           two is showing. The submit carries the form's id because the band
           is outside the form's own subtree. */
        <DialogFooter>
          <UIButton variant="secondary" onClick={onCancel}>
            Cancel
          </UIButton>
          <UIButton
            variant={side === "sell" ? "destructiveSolid" : "primary"}
            type="submit"
            form={TRADE_FORM_ID}
          >
            Review {side === "sell" ? "sell" : "buy"}
          </UIButton>
        </DialogFooter>
      )}
      <Form
        hidden={showSummarytModal}
        initialValues={{
          order_type: "Market",
          order_quantity: 1,
          tif: "DAY",
          take_profit: 0,
          stop_loss: 0,
        }}
        form={form}
        /* Named, so the dialog's own footer can submit it. The two buttons
           used to sit INSIDE the form as the last row of the grid — a
           dialog whose actions were part of its body, at a different width
           and on a different line from every other dialog's. */
        id={TRADE_FORM_ID}
        style={{ width: "100%" }}
        onFinish={handleSubmit}
      >
        <Row gutter={[16, 16]}>
          {/* The price, not the prospectus. The symbol-info embed opens with
              the ticker set at 28px — repeating the dialog's own title — and
              then a row of fundamentals: upcoming earnings, EPS, market cap,
              dividend yield, P/E. None of those is part of deciding how many
              shares to buy, and all of them are on the security page this
              dialog was opened from. Cropped to the identity and the price,
              and rendered smaller, so the top of the dialog states what the
              order is priced against and stops. */}
          <Col span={24}>
            <SymbolBox>
              <SymbolInfoWidget symbol={tickerSymbol} />
            </SymbolBox>
          </Col>
          {/* What the order is bounded by, set as a reading rather than as
              two bold runs of prose. It is the number the amount field is
              validated against, so it belongs in the same treatment as the
              other figures in the product's dialogs. */}
          <Col span={24}>
            <Balance>
              <span>Available cash</span>
              <b>${formatMoney(balance)}</b>
            </Balance>
          </Col>
          <Col span={24}>
            <FormItem name="order_type">
              {isMobile ? (
                <Select
                  size="field"
                  options={orderTypeOptions}
                  onChange={handleSelectOrderType}
                />
              ) : (
                <RadioGroup
                  size="large"
                  style={{ width: "100%" }}
                  onChange={handleChangeOrderType}
                  optionType="button"
                  buttonStyle="solid"
                >
                  {optionsOrderType.map((option) => (
                    <Radio.Button value={option.value}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "10px",
                        }}
                      >
                        {option.label}
                        <Tooltip title={option.definition} key={option.value}>
                          <HugeIcon
                            icon={InformationCircleIcon}
                            style={{ fontSize: "12px" }}
                          />
                        </Tooltip>
                      </div>
                    </Radio.Button>
                  ))}
                </RadioGroup>
              )}
            </FormItem>
          </Col>
          <Col span={24}>
            {tickePrice !== 0 && (
              <FormItem
                name="order_quantity"
                rules={[
                  ...sellBuyValidationRules.quntity,
                  {
                    validator: amountValidation,
                  },
                ]}
              >
                <StockTextInput
                  stockPrice={tickePrice}
                  leftLabel="Number of shares"
                  rightLabel="Indicative value"
                  onChange={handleChangeSharesNumber}
                  maxQty={quantityCap(side, stockInfos?.maxQty)}
                />
              </FormItem>
            )}
          </Col>
          {/* {["Stop", "StopLimit", "OCO"].includes(order_type) && (
                        <Col span={24}>
                            <FormItem
                                name="order_stop_price"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter the stop loss number!",
                                    },
                                    {
                                        validator: stopPriceValidation,
                                    },
                                ]}
                            >
                                <StockNumberInput
                                    leftLabel="Stop price"
                                    rightLabel={orderTypeLabel[order_type].first}
                                    onChange={handleChangeStopPrice}
                                />
                            </FormItem>
                        </Col>
                    )} */}
          {["Limit", "StopLimit", "OCO"].includes(order_type) && (
            <Col xs={24} sm={24} md={12} lg={12}>
              <FormItem
                name="order_limit_price"
                rules={[
                  {
                    required: true,
                    message: "Please enter the limit price number!",
                  },
                  {
                    validator: limitPriceValidation,
                  },
                ]}
              >
                <StockNumberInput
                  leftLabel="Limit price"
                  rightLabel={orderTypeLabel[order_type].second}
                  onChange={handleChangeLimitPrice}
                />
              </FormItem>
            </Col>
          )}
          {/* {order_type === "TrailingStop" && (
                        <>
                            <Col span={24}>
                                <FormItem
                                    name="trailingStep"
                                    rules={sellBuyValidationRules.price}
                                >
                                    <StockNumberInput
                                        leftLabel="Trailing step"
                                        rightLabel={orderTypeLabel[order_type].second}
                                        onChange={handleChangeTrailingStep}
                                    />
                                </FormItem>
                            </Col>
                        </>
                    )} */}
          {["Limit", "Stop", "StopLimit", "OCO", "TrailingStop"].includes(
            order_type,
          ) && (
            <>
              <Col xs={24} sm={24} md={12} lg={12}>
                <InputLabelDescription
                  label="Time in force (TIF)"
                  description={tifDefinition}
                  htmlFor="tif"
                />
                <FormItem name="tif">
                  {/* The code is what the trigger shows, because it is what
                      the reader chose and what every order confirmation and
                      order list states; the words are in the menu, where
                      they are being read for the first time. */}
                  <Select
                    size="field"
                    dropdownClassName="fina-select-stacked"
                    optionLabelProp="value"
                    options={tifOptions}
                    onChange={handleSelectTif}
                  />
                </FormItem>
              </Col>
              {/* <Col xs={24} sm={24} md={12} lg={12} >
                                    <Text style={{ color: "#0D4BA8", paddingLeft: 10 }}>
                                        Duration
                                    </Text>
                                    <FormItem
                                        name="duration"
                                        rules={[{ validator: durationValidation }]}
                                    >
                                        <DatePicker
                                            value={dateAndTime}
                                            showTime
                                            disabledDate={disabledDate}
                                            allowClear={false}
                                            size="large"
                                            onOk={onOk}
                                            format={"YYYY-MM-DD HH:mm z"}
                                            style={{ width: "100%" }}
                                            disabled={!["GTD", "GAT"].includes(tif)}
                                        />
                                    </FormItem>
                                </Col> */}
            </>
          )}
          {/* {!["OCO"].includes(order_type) && (
                        <>
                            <Col span={24}>
                                <FormItem
                                    name="take_profit"
                                >
                                    <StockProfitLossInput
                                        type="profit"
                                        leftLabel="Take profit"
                                        rightLabel={
                                            <RadioGroupPL
                                                size="small"
                                                onChange={handleChangeProfitLossType}
                                                value={profitLossType}
                                            >
                                                {optionsProfitLossType.map((option) => (
                                                    <Radio.Button value={option.value} key={option.value}>
                                                        &#x2022; {option.label}
                                                    </Radio.Button>
                                                ))}
                                            </RadioGroupPL>
                                        }
                                        profitLossType={profitLossType}
                                        initialAmount={amount}
                                        onChange={handleChangeTakeProfit}
                                    />
                                </FormItem>
                            </Col>
                            <Col span={24}>
                                <FormItem name="stop_loss"
                                >
                                    <StockProfitLossInput
                                        type="loss"
                                        leftLabel="Stop loss"
                                        profitLossType={profitLossType}
                                        initialAmount={amount}
                                        onChange={handleChangeStopLoss}
                                    />
                                </FormItem>
                            </Col>
                        </>
                    )} */}
        </Row>
      </Form>
    </>
  );
};
